import React, { useEffect, useRef, useState } from 'react';
import {
  Plus,
  Copy,
  Scissors,
  Clipboard,
  Trash2,
  Edit3,
  Box,
  Square,
  Type,
  ChevronRight,
} from 'lucide-react';
import { useAssetPreviewStore } from '../../store/assetPreviewStore';
import { cn } from '../../utils/cn';

interface PrefabContextMenuProps {
  targetId: string | null;
  instanceId: string;
  x: number;
  y: number;
  onClose: () => void;
  onRename: (id: string) => void;
}

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  action?: () => void;
  shortcut?: string;
  divider?: boolean;
  disabled?: boolean;
  submenu?: SubMenuItem[];
}

interface SubMenuItem {
  label: string;
  icon: React.ReactNode;
  action: () => void;
}

export const PrefabContextMenu: React.FC<PrefabContextMenuProps> = ({
  targetId,
  instanceId,
  x,
  y,
  onClose,
  onRename,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    prefabStates,
    addPrefabChild,
    deletePrefabObject,
    copyPrefabObject,
    pastePrefabObject,
  } = useAssetPreviewStore();

  const prefabState = prefabStates[instanceId];
  const targetObject = targetId ? prefabState?.gameObjects[targetId] : null;
  const hasSelection = (prefabState?.selectedIds.length ?? 0) > 0;
  const hasClipboard = prefabState?.clipboardData !== null;
  const isRoot = targetObject?.parentId === null;

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
    };
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Adjust position to stay within viewport
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      if (rect.right > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 10;
      }

      if (rect.bottom > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 10;
      }

      menuRef.current.style.left = `${Math.max(0, adjustedX)}px`;
      menuRef.current.style.top = `${Math.max(0, adjustedY)}px`;
    }
  }, [x, y]);

  const createObjectSubmenu: SubMenuItem[] = [
    {
      label: 'Empty Object',
      icon: <Box className="w-4 h-4" />,
      action: () => {
        addPrefabChild(instanceId, targetId, 'empty');
        onClose();
      },
    },
    {
      label: 'Sprite',
      icon: <Square className="w-4 h-4" />,
      action: () => {
        addPrefabChild(instanceId, targetId, 'sprite');
        onClose();
      },
    },
    {
      label: 'Text',
      icon: <Type className="w-4 h-4" />,
      action: () => {
        addPrefabChild(instanceId, targetId, 'text');
        onClose();
      },
    },
    {
      label: 'Container',
      icon: <Box className="w-4 h-4" />,
      action: () => {
        addPrefabChild(instanceId, targetId, 'container');
        onClose();
      },
    },
  ];

  const menuItems: MenuItem[] = [
    {
      label: 'Create',
      icon: <Plus className="w-4 h-4" />,
      submenu: createObjectSubmenu,
    },
    { label: '', icon: null, divider: true },
    {
      label: 'Cut',
      icon: <Scissors className="w-4 h-4" />,
      shortcut: 'Ctrl+X',
      disabled: !hasSelection || isRoot,
      action: () => {
        // Cut is copy + mark for delete on paste
        copyPrefabObject(instanceId);
        onClose();
      },
    },
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      shortcut: 'Ctrl+C',
      disabled: !hasSelection,
      action: () => {
        copyPrefabObject(instanceId);
        onClose();
      },
    },
    {
      label: 'Paste',
      icon: <Clipboard className="w-4 h-4" />,
      shortcut: 'Ctrl+V',
      disabled: !hasClipboard,
      action: () => {
        pastePrefabObject(instanceId, targetId);
        onClose();
      },
    },
    { label: '', icon: null, divider: true },
    {
      label: 'Rename',
      icon: <Edit3 className="w-4 h-4" />,
      shortcut: 'F2',
      disabled: !targetId,
      action: () => {
        if (targetId) {
          onRename(targetId);
        }
        onClose();
      },
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      shortcut: 'Delete',
      disabled: !targetId || isRoot,
      action: () => {
        if (targetId && !isRoot) {
          deletePrefabObject(instanceId, targetId);
        }
        onClose();
      },
    },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-[100] min-w-48 py-1"
      style={{
        left: x,
        top: y,
      }}
    >
      {menuItems.map((item, index) => {
        if (item.divider) {
          return <div key={`divider-${index}`} className="h-px bg-zinc-700 my-1" />;
        }

        const hasSubmenu = item.submenu && item.submenu.length > 0;
        const isSubmenuActive = activeSubmenu === item.label;

        const handleMouseEnter = () => {
          if (submenuTimeoutRef.current) {
            clearTimeout(submenuTimeoutRef.current);
            submenuTimeoutRef.current = null;
          }
          if (hasSubmenu) {
            setActiveSubmenu(item.label);
          }
        };

        const handleMouseLeave = () => {
          if (hasSubmenu) {
            // Delay closing to allow mouse to move to submenu
            submenuTimeoutRef.current = setTimeout(() => {
              setActiveSubmenu(null);
            }, 150);
          }
        };

        const handleClick = (e: React.MouseEvent) => {
          if (hasSubmenu) {
            // Toggle submenu on click instead of executing action
            e.preventDefault();
            e.stopPropagation();
            setActiveSubmenu(isSubmenuActive ? null : item.label);
          } else if (item.action) {
            item.action();
          }
        };

        return (
          <div
            key={item.label}
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              onClick={handleClick}
              disabled={item.disabled}
              className={cn(
                'w-full px-3 py-1.5 flex items-center gap-2 text-left text-sm transition-colors',
                item.disabled
                  ? 'text-zinc-600 cursor-not-allowed'
                  : 'text-zinc-200 hover:bg-zinc-700',
                isSubmenuActive && 'bg-zinc-700'
              )}
            >
              <span className={cn('w-4', item.disabled && 'opacity-50')}>
                {item.icon}
              </span>
              <span className="flex-1">{item.label}</span>
              {item.shortcut && (
                <span className="text-xs text-zinc-500 ml-4">{item.shortcut}</span>
              )}
              {hasSubmenu && (
                <ChevronRight className="w-4 h-4 text-zinc-500" />
              )}
            </button>

            {/* Submenu */}
            {hasSubmenu && isSubmenuActive && (
              <div
                className="absolute left-full top-0 ml-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl min-w-44 py-1 z-[101]"
                onMouseEnter={() => {
                  if (submenuTimeoutRef.current) {
                    clearTimeout(submenuTimeoutRef.current);
                    submenuTimeoutRef.current = null;
                  }
                }}
                onMouseLeave={() => {
                  setActiveSubmenu(null);
                }}
              >
                {item.submenu!.map((subItem) => (
                  <button
                    key={subItem.label}
                    onClick={subItem.action}
                    className="w-full px-3 py-1.5 flex items-center gap-2 text-left text-sm text-zinc-200 hover:bg-zinc-700 transition-colors"
                  >
                    <span className="w-4">{subItem.icon}</span>
                    <span className="flex-1">{subItem.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
